import { useState, useEffect } from "react";
import * as Yup from "yup";
import { Link as RouterLink, useNavigate } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// routes
import { PATH_AUTH } from "../../routes/paths";
// auth
import { postLogin, logout } from "src/redux/slices/auth";
// components
import Iconify from "../../components/iconify";
import FormProvider, { RHFTextField } from "../../components/hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { deleteAllState } from "src/redux/slices/state";

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function AuthLoginForm() {
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();

  const authState = useSelector((state: RootState) => state.auth);

  const navigate = useNavigate();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required("O email é obrigatório")
      .email("Digite um email valido"),
    password: Yup.string().required("Senha é obrigatória"),
  });

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      dispatch(postLogin({ email: data.email, password: data.password }));
    } catch (error) {
      console.error(error);
      reset();
      setError("afterSubmit", {
        ...error,
        message: error.message || error,
      });
    }
  };

  useEffect(() => {
    if (authState.error?.length) {
      setError("afterSubmit", {});
    }
  }, [authState.error]);

  useEffect(() => {
    if (authState.accessToken?.length && authState.refreshToken?.length) {
      navigate("/dashboard");
    } else {
      dispatch(logout());
      dispatch(deleteAllState());
    }
  }, [authState.accessToken, authState.refreshToken]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <RHFTextField name="email" label="Email" />

        <RHFTextField
          name="password"
          label="Senha"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link
          component={RouterLink}
          to={PATH_AUTH.resetPassword}
          variant="body2"
          color="inherit"
          underline="none"
          fontWeight={600}
        >
          Esqueceu a senha?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={authState.isLoading}
        sx={{
          mt: 2,
          bgcolor: "primary.main",
          color: (theme) =>
            theme.palette.mode === "light" ? "text.white" : "grey.800",
          "&:hover": {
            bgcolor: "primary.main",
            color: (theme) =>
              theme.palette.mode === "light" ? "text.white" : "grey.800",
          },
        }}
      >
        Login
      </LoadingButton>
      <br />
      <br />
      {!!errors.afterSubmit && (
        <div style={{ position: "relative" }}>
          <Alert
            style={{ position: "absolute", left: 0, right: 0, top: 30 }}
            severity="error"
          >
            Login incorreto
          </Alert>
        </div>
      )}
    </FormProvider>
  );
}
